import type {
  BrandItem,
  ColorItem,
  LensColorCategory,
  LensShowItem,
  LensShowResponse,
} from '../types';

const isBrandLens = (lensType?: LensShowItem) => Number(lensType?.merge_brand) === 1;

export function getLensColorCategoryViewModels(lensType?: LensShowItem): LensColorCategory[] {
  if (isBrandLens(lensType) || !(lensType?.color_category?.length > 1)) {
    return [];
  }

  return lensType.color_category || [];
}

export function shouldShowLensColorCategories(lensType?: LensShowItem) {
  return getLensColorCategoryViewModels(lensType).length > 1;
}

export function getLensColorCategoryByTitle(
  lensType?: LensShowItem,
  categoryTitle?: string | null,
): LensColorCategory | null {
  if (!categoryTitle) {
    return null;
  }

  return (
    getLensColorCategoryViewModels(lensType).find((category) => category.title === categoryTitle) ??
    null
  );
}

export function getFlattenedLensColorCategoryColors(lensType?: LensShowItem) {
  return getLensColorCategoryViewModels(lensType).flatMap((category) => category.color_list);
}

export function getLensColorCategoryColorByLensColor(
  lensType: LensShowItem,
  lensColor?: string | null,
): ColorItem | null {
  if (!lensColor) {
    return null;
  }

  return (
    getFlattenedLensColorCategoryColors(lensType).find(
      (colorItem) => colorItem.lens_color === lensColor,
    ) ?? null
  );
}

export function resolveLensColorCategoryClickColor(category: LensColorCategory): ColorItem | null {
  return (
    category.color_list?.find(
      (colorItem) => colorItem.lens_color === category.default_lens_color,
    ) ??
    category.color_list?.[0] ??
    null
  );
}

export function getLensTypeDefaultColorItem(lensType: LensShowItem): ColorItem | null {
  const categories = getLensColorCategoryViewModels(lensType);
  if (categories.length > 1) {
    const colors = getFlattenedLensColorCategoryColors(lensType);
    return (
      colors.find((colorItem) => colorItem.lens_color === lensType.default_lens_color) ??
      colors[0] ??
      null
    );
  }

  if (!Array.isArray(lensType.color_list) || !lensType.color_list.length) {
    return null;
  }

  return (
    lensType.color_list.find((colorItem) => colorItem.lens_color === lensType.default_lens_color) ??
    lensType.color_list[0]
  );
}

function setBrandDefaultColor(brand: BrandItem) {
  if (brand.color_list?.length) {
    brand.default_lens_color = brand.color_list[0].lens_color;
  }
}

function setLensColorCategoryDefaultColor(category: LensColorCategory) {
  if (category.color_list?.length) {
    category.default_lens_color = category.color_list[0].lens_color;
  }
}

function ensureLensTypeColorList(lensType: LensShowItem) {
  if (shouldShowLensColorCategories(lensType)) {
    lensType.color_category?.forEach(setLensColorCategoryDefaultColor);
    return;
  }

  const defaultColor = getLensTypeDefaultColorItem(lensType);
  if (defaultColor) {
    lensType.default_lens_color = defaultColor.lens_color;
  }
}

export function prepareLensShowColorDefaults(lensData: LensShowResponse): LensShowResponse {
  lensData.forEach((lensType) => {
    if (isBrandLens(lensType)) {
      lensType.brand_list?.forEach(setBrandDefaultColor);
      return;
    }

    ensureLensTypeColorList(lensType);
  });

  return lensData;
}
